(() => {
  "use strict";

  const NS = "http://www.w3.org/2000/svg";
  const FACE_SIZE = 150;
  const CELL = 50;
  const FACE_GAP = 12;
  const FACE_POSITIONS = {
    U: [172, 10],
    L: [10, 172],
    F: [172, 172],
    R: [334, 172],
    B: [496, 172],
    D: [172, 334]
  };
  const FACE_ORDER = ["U", "L", "F", "R", "B", "D"];
  const CORNER_CELLS = [[0, 0], [0, 2], [2, 2], [2, 0]];
  const EDGE_CELLS = [[0, 1], [1, 2], [2, 1], [1, 0]];
  const COLOURS = {
    inactive: "#e5e9f0",
    active: "#ffffff",
    centre: "#172033",
    line: "#172033",
    buffer: "#4361ee",
    highlight: "#25a66f",
    cycle: "#ffcf33",
    cycleStrong: "#ff8a2a",
    arrow: "#d64045"
  };
  let svgCount = 0;

  const svgElement = (name, attributes = {}) => {
    const node = document.createElementNS(NS, name);
    Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
    return node;
  };

  const normaliseLetters = (value) => String(value || "")
    .toUpperCase()
    .replace(/[^A-X]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  function scheme() {
    const output = {};
    FACE_ORDER.forEach((face, faceIndex) => {
      const first = faceIndex * 4;
      output[face] = Array.from({ length: 4 }, (_, index) => String.fromCharCode(65 + first + index));
    });
    return output;
  }

  function stickerMap(kind = "corners") {
    const map = {};
    const letters = scheme();
    const cells = kind === "edges" ? EDGE_CELLS : CORNER_CELLS;
    FACE_ORDER.forEach((face) => {
      const [faceX, faceY] = FACE_POSITIONS[face];
      letters[face].forEach((letter, index) => {
        const [row, column] = cells[index];
        map[letter] = {
          face,
          row,
          column,
          x: faceX + column * CELL + CELL / 2,
          y: faceY + row * CELL + CELL / 2
        };
      });
    });
    return map;
  }

  function shortenLine(start, end, amount = 17) {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const length = Math.hypot(dx, dy) || 1;
    return {
      start: { x: start.x + dx / length * amount, y: start.y + dy / length * amount },
      end: { x: end.x - dx / length * amount, y: end.y - dy / length * amount }
    };
  }

  function arrowPath(start, end, index) {
    const shortened = shortenLine(start, end);
    const dx = shortened.end.x - shortened.start.x;
    const dy = shortened.end.y - shortened.start.y;
    const length = Math.hypot(dx, dy) || 1;
    const bend = Math.min(34, Math.max(14, length * 0.09)) * (index % 2 ? -1 : 1);
    const midpoint = {
      x: (shortened.start.x + shortened.end.x) / 2 - dy / length * bend,
      y: (shortened.start.y + shortened.end.y) / 2 + dx / length * bend
    };
    return `M ${shortened.start.x.toFixed(1)} ${shortened.start.y.toFixed(1)} Q ${midpoint.x.toFixed(1)} ${midpoint.y.toFixed(1)} ${shortened.end.x.toFixed(1)} ${shortened.end.y.toFixed(1)}`;
  }

  function makeSvg(config = {}) {
    const mode = ["corners", "edges", "both"].includes(config.mode) ? config.mode : "corners";
    const cycleType = config.cycleType === "edges" ? "edges" : "corners";
    const buffers = new Set(normaliseLetters(config.buffer));
    const highlights = new Set(normaliseLetters(config.highlight));
    const cornerHighlights = new Set(normaliseLetters(config.cornerHighlight));
    const edgeHighlights = new Set(normaliseLetters(config.edgeHighlight));
    const cycle = normaliseLetters(config.cycle);
    const cornerCycle = normaliseLetters(config.cornerCycle);
    const edgeCycle = normaliseLetters(config.edgeCycle);
    const cycleLetters = new Set([...cycle, ...cornerCycle, ...edgeCycle]);
    const lettersByFace = scheme();
    const cornerMap = stickerMap("corners");
    const edgeMap = stickerMap("edges");
    const cycleMap = cycleType === "edges" ? edgeMap : cornerMap;
    const markerId = `bld-arrow-${++svgCount}`;
    const svg = svgElement("svg", {
      viewBox: "0 0 656 496",
      role: "img",
      "aria-label": config.label || `${mode} blindfolded lettering diagram`
    });
    svg.classList.add("bld-svg");
    svg.style.width = "100%";
    svg.style.height = "auto";
    svg.style.display = "block";

    const defs = svgElement("defs");
    const marker = svgElement("marker", {
      id: markerId,
      markerWidth: "9",
      markerHeight: "9",
      refX: "7.5",
      refY: "3.5",
      orient: "auto",
      markerUnits: "strokeWidth"
    });
    marker.append(svgElement("path", { d: "M0,0 L0,7 L8,3.5 z", fill: COLOURS.arrow }));
    defs.append(marker);
    defs.append(svgElement("style"));
    defs.lastChild.textContent = ".bld-cell{stroke:#172033;stroke-width:2}.bld-letter{font:800 20px Arial,sans-serif;text-anchor:middle;dominant-baseline:middle;fill:#172033}.bld-letter--light{fill:#fff}.bld-face-label{font:900 18px Arial,sans-serif;text-anchor:middle;dominant-baseline:middle;fill:#fff}.bld-arrow{fill:none;stroke:#d64045;stroke-width:4;stroke-linecap:round;stroke-dasharray:8 5;opacity:.88}";
    svg.append(defs);

    const cellGroup = svgElement("g");
    const arrowGroup = svgElement("g");
    const labelGroup = svgElement("g");

    FACE_ORDER.forEach((face) => {
      const [faceX, faceY] = FACE_POSITIONS[face];
      const faceLetters = lettersByFace[face];
      for (let row = 0; row < 3; row++) {
        for (let column = 0; column < 3; column++) {
          const isCentre = row === 1 && column === 1;
          const cornerIndex = CORNER_CELLS.findIndex(([r, c]) => r === row && c === column);
          const edgeIndex = EDGE_CELLS.findIndex(([r, c]) => r === row && c === column);
          const cornerLetter = cornerIndex >= 0 ? faceLetters[cornerIndex] : "";
          const edgeLetter = edgeIndex >= 0 ? faceLetters[edgeIndex] : "";
          const letter = mode === "corners" ? cornerLetter : mode === "edges" ? edgeLetter : cornerLetter || edgeLetter;
          let fill = isCentre ? COLOURS.centre : letter ? COLOURS.active : COLOURS.inactive;
          const isTypedHighlight = (cornerLetter && cornerHighlights.has(cornerLetter)) || (edgeLetter && edgeHighlights.has(edgeLetter));
          if (letter && cycleLetters.has(letter)) fill = COLOURS.cycle;
          if (letter && (highlights.has(letter) || isTypedHighlight)) fill = COLOURS.highlight;
          if (letter && buffers.has(letter)) fill = COLOURS.buffer;
          cellGroup.append(svgElement("rect", {
            x: faceX + column * CELL,
            y: faceY + row * CELL,
            width: CELL,
            height: CELL,
            rx: "3",
            fill,
            class: "bld-cell"
          }));
          if (isCentre) {
            const faceLabel = svgElement("text", {
              x: faceX + column * CELL + CELL / 2,
              y: faceY + row * CELL + CELL / 2,
              class: "bld-face-label"
            });
            faceLabel.textContent = face;
            labelGroup.append(faceLabel);
          } else if (letter) {
            const letterLabel = svgElement("text", {
              x: faceX + column * CELL + CELL / 2,
              y: faceY + row * CELL + CELL / 2,
              class: `bld-letter${buffers.has(letter) || highlights.has(letter) || isTypedHighlight ? " bld-letter--light" : ""}`
            });
            letterLabel.textContent = letter;
            labelGroup.append(letterLabel);
          }
        }
      }
    });

    const drawCycle = (letters, map, indexOffset = 0) => {
      for (let index = 0; index < letters.length - 1; index++) {
        const start = map[letters[index]];
        const end = map[letters[index + 1]];
        if (!start || !end || start === end) continue;
        arrowGroup.append(svgElement("path", {
          d: arrowPath(start, end, index + indexOffset),
          class: "bld-arrow",
          "marker-end": `url(#${markerId})`
        }));
      }
    };

    drawCycle(cycle, cycleMap);
    drawCycle(cornerCycle, cornerMap);
    drawCycle(edgeCycle, edgeMap, 1);

    svg.append(cellGroup, arrowGroup, labelGroup);
    return svg;
  }

  function configFromElement(host) {
    return {
      mode: host.dataset.mode || "corners",
      cycleType: host.dataset.cycleType || host.dataset.mode || "corners",
      buffer: host.dataset.buffer || "",
      highlight: host.dataset.highlight || "",
      cornerHighlight: host.dataset.cornerHighlight || "",
      edgeHighlight: host.dataset.edgeHighlight || "",
      cycle: host.dataset.cycle || "",
      cornerCycle: host.dataset.cornerCycle || "",
      edgeCycle: host.dataset.edgeCycle || "",
      label: host.dataset.label || ""
    };
  }

  function renderElement(host) {
    host.classList.add("bld-host");
    host.replaceChildren(makeSvg(configFromElement(host)));
    host.dataset.bldReady = "true";
    return true;
  }

  function renderAll(root = document) {
    root.querySelectorAll(".bld-diagram").forEach(renderElement);
  }

  window.SpeedcubingBldDiagrams = {
    faceOrder: FACE_ORDER,
    scheme,
    stickerMap,
    normaliseLetters,
    makeSvg,
    renderElement,
    renderAll
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => renderAll());
  else renderAll();
  document.addEventListener("shopify:section:load", (event) => renderAll(event.target));
})();
